import { Feature, Point } from 'geojson';
import { Subject } from 'rxjs';

import { SnMapClusterPoint } from '@lib/api';
import { IFsMap } from '@lib/geo/core';
import { SnLonLat, SnLonLatLike } from '@lib/geo/helpers';
import { IMapMarkerStyleOptions } from '@lib/geo/map/core/marker/marker-options';
import { SnBadgeConfig } from '@lib/ui/badge';

import { EsriMap } from '../../map';
import { EsriModules } from '../../tokens/esri.tokens';
import { IAdvancedEsriMarkerOptions } from './advanced-marker-options';
import { IEsriMarker } from './interfaces/esri-marker.interface';
import { IEsriMapMarkerLabel, IEsriMapMarkerIconLabel, EsriMapMarkerIcon } from './marker-options';
import { EsriMarkerType } from './marker-types';

export class FsAdvancedEsriMarker implements IEsriMarker {
  public updated: number = Date.now();
  public id: string;
  public feature: Feature<Point>;
  public icon: EsriMapMarkerIcon | undefined;
  public size: string;
  public color: string;
  public rotation: number;
  public outline: boolean;
  public iconLabel: IEsriMapMarkerIconLabel | undefined;
  public styles: IMapMarkerStyleOptions | undefined;
  public cluster?: SnMapClusterPoint;
  public content: HTMLElement = document.createElement('div');
  public badgePosition: Record<string, string>;
  public layer: string | undefined;
  public organization: string | undefined;

  // Core marker properties
  public marker: InstanceType<EsriModules['Graphic']>;
  public draggable: boolean = false;
  public badge: HTMLElement | undefined;
  public address?: string;

  // Animation properties
  private _followMode: boolean = false;
  private _destination?: SnLonLat;
  private _deltaLat: number = 0;
  private _deltaLng: number = 0;
  private _expectedMoves: number = 0;
  private _actualMoves: number = 0;
  private _isAnimating: boolean = false;

  // Private properties
  private _options: IAdvancedEsriMarkerOptions;
  private _map?: EsriMap;
  private _zIndex: number = 0;
  private _title: string = '';
  private _visible: boolean = true;
  private _style?: IMapMarkerStyleOptions;
  private _badge?: SnBadgeConfig;
  private _label?: IEsriMapMarkerLabel;
  private _type?: EsriMarkerType;

  // Observables for events
  private readonly _latLngChange$ = new Subject<SnLonLat>();
  private readonly _removed$ = new Subject<void>();
  private readonly _destroy$ = new Subject<void>();

  // Timeouts for animations and pulses
  public timeouts: NodeJS.Timeout[] = [];
  public interval?: number;
  public pulseInterval?: number;

  constructor(private readonly esri: EsriModules, options?: IAdvancedEsriMarkerOptions) {
    this._options = {
      size: '24px',
      color: '#fff',
      rotation: 0,
      outline: false,
      ...options,
    };

    this.id = this._options.id || this._generateId();
    this.feature = this._options.feature;
    this.icon = this._options.icon as EsriMapMarkerIcon | undefined;
    this.size = this._options.size || '24px';
    this.color = this._options.color || '#fff';
    this.rotation = this._options.rotation || 0;
    this.iconLabel = this._options.iconLabel;
    this.styles = this._options.styles;
    this.cluster = this._options.cluster;
    this.content = this._options.content || this.content;
    this.outline = this._options.outline || false;
    this.layer = this._options.layer;
    this.organization = this._options.organization;
    this.draggable = this._options.draggable || false;
    this._title = this._options.title || '';
    this._zIndex = this._options.zIndex || 0;

    this.badgePosition = {
      right: '-6px',
      bottom: '-2px',
      top: 'auto',
      left: 'auto',
      ...this._options.badgePosition,
    };

    this._createMarker();

    // Add to map if provided
    if (this._options.map) {
      this.addTo(this._options.map);
    }

    // Handle cluster markers
    if (this.cluster && this._options.isAxonLayer) {
      this._createClusterMarker();
    } else if (this._isImageIcon(this.icon)) {
      this._createImageMarker();
    } else {
      this._createIconMarker();
    }
  }

  // Core IEsriMarker interface methods
  public getMarker(): InstanceType<EsriModules['Graphic']> {
    return this.marker;
  }

  public addTo(map: EsriMap): void {
    this._map = map;
    if (this.marker && map.mapView) {
      map.mapView.graphics.add(this.marker);
    }
  }

  public remove(): void {
    if (this._map && this.marker) {
      this._map.mapView.graphics.remove(this.marker);
    }
    this._cleanup();
    this._removed$.next();
  }

  // Position methods
  public getLonLat(): SnLonLat {
    if (this.marker?.geometry) {
      const geom = this.marker.geometry as InstanceType<EsriModules['Point']>;
      if (geom.longitude !== undefined && geom.latitude !== undefined) {
        return new SnLonLat(geom.longitude, geom.latitude);
      }
    }
    return new SnLonLat(0, 0);
  }

  public setLonLat(lonLat: SnLonLatLike): void {
    const position = SnLonLat.convert(lonLat);
    if (this.marker) {
      this.marker.geometry = {
        type: 'point',
        longitude: position.lon,
        latitude: position.lat,
      } as InstanceType<EsriModules['Point']>;
    }
    this._latLngChange$.next(position);
  }

  // Title methods
  public getTitle(): string {
    return this._title;
  }

  public setTitle(title: string): void {
    this._title = title;
    if (this.marker) {
      this.marker.attributes = { ...this.marker.attributes, title };
    }
  }

  // Draggable methods
  public getDraggable(): boolean {
    return this.draggable;
  }

  public setDraggable(draggable: boolean): void {
    this.draggable = draggable;
    // ESRI doesn't have built-in draggable like Google Maps
    // This would need to be implemented with event handlers
  }

  // Z-index methods
  public getZIndex(): number {
    return this._zIndex;
  }

  public setZIndex(zIndex: number): void {
    this._zIndex = zIndex;
    // ESRI uses symbol order in graphics layer for z-index
  }

  // Icon methods
  public getIcon(): EsriMapMarkerIcon | undefined {
    return this.icon;
  }

  public setIcon(icon: EsriMapMarkerIcon): void {
    this.icon = icon;
    this._updateMarkerSymbol();
  }

  // Style methods
  public getStyle(): IMapMarkerStyleOptions | undefined {
    return this._style;
  }

  public setStyle(style: IMapMarkerStyleOptions): void {
    this._style = style;
    this.styles = { ...this.styles, ...style };
    this._updateMarkerSymbol();
  }

  // Badge methods
  public getBadge(): SnBadgeConfig | undefined {
    return this._badge;
  }

  public setBadge(badge: SnBadgeConfig): void {
    this._badge = badge;
    this._createBadge();
  }

  // Label methods
  public getLabel(): IEsriMapMarkerLabel | undefined {
    return this._label;
  }

  public setLabel(label: string | IEsriMapMarkerLabel): void {
    if (typeof label === 'string') {
      this._label = { text: label };
    } else {
      this._label = label;
    }
    this._updateMarkerSymbol();
  }

  // Rotation methods
  public getRotation(): number {
    return this.rotation;
  }

  public setRotation(rotation: number): void {
    this.rotation = rotation;
    this._updateMarkerSymbol();
  }

  // Visibility methods
  public setVisible(visible: boolean): void {
    this._visible = visible;
    if (this.marker) {
      this.marker.visible = visible;
    }
  }

  public getVisible(): boolean {
    return this._visible;
  }

  // Animation/Movement methods - based on Google marker implementation
  public setFollowMode(followMode: boolean): void {
    this._followMode = followMode;
  }

  public getFollowMode(): boolean {
    return this._followMode;
  }

  public setDestination(dest: SnLonLatLike): void {
    if (this._destination) {
      // Set current position to previous destination before updating (like Google implementation)
      this.setLonLat(this._destination);
    }
    this._destination = SnLonLat.convert(dest);
  }

  public setDeltas(seconds: number): void {
    if (!this._destination) return;

    const current = this.getLonLat();
    if (!current) return;

    // Based on Google implementation: 10 moves per second for smooth animation
    this._expectedMoves = 10 * seconds;
    this._actualMoves = 0;
    this._deltaLat = (this._destination.lat - current.lat) / this._expectedMoves;
    this._deltaLng = (this._destination.lon - current.lon) / this._expectedMoves;
  }

  public move(): void {
    if (!this._destination) return;

    if (this._actualMoves >= this._expectedMoves) {
      this.setLonLat(this._destination);
      if (this._followMode && this._map) {
        // Use existing panTo implementation (async)
        this._map.panTo(this._destination).catch(console.error);
      }
      return;
    }

    this._actualMoves++;
    const current = this.getLonLat();
    if (!current) return;

    // Check if marker is within map bounds (similar to Google implementation)
    if (!this.isWithinMapBounds()) {
      this._actualMoves = this._expectedMoves;
      this.setLonLat(this._destination);
      if (this._followMode && this._map) {
        // Use existing panTo implementation (async)
        this._map.panTo(this._destination).catch(console.error);
      }
      return;
    }

    let newLat = current.lat + this._deltaLat;
    if (current.lat === this._destination.lat) {
      newLat = this._destination.lat;
    }

    let newLng = current.lon + this._deltaLng;
    if (current.lon === this._destination.lon) {
      newLng = this._destination.lon;
    }

    const newLonLat = new SnLonLat(newLng, newLat);
    this.setLonLat(newLonLat);

    if (this._followMode && this._map) {
      // Use existing panTo implementation (async)
      this._map.panTo(newLonLat).catch(console.error);
    }
  }

  public animateSetLonLat(seconds: number, moveto: SnLonLatLike): void {
    const destination = SnLonLat.convert(moveto);

    // Check if map contains destination (similar to Google implementation)
    if (this._map && !this._map.contains(destination)) {
      // Clear any existing animation interval
      this.timeouts.forEach((timeout) => clearTimeout(timeout));
      this.timeouts = [];
      this.setLonLat(destination);
      return;
    }

    const current = this.getLonLat();
    if (!current) return;

    // If already at destination, no need to animate
    if (destination.lat === current.lat && destination.lon === current.lon) {
      return;
    }

    this.setDestination(moveto);
    this.setDeltas(seconds);
    this._isAnimating = true;

    // Clear any existing animation
    this.timeouts.forEach((timeout) => clearTimeout(timeout));
    this.timeouts = [];

    // Start animation with delay based on Google implementation (10ms * seconds)
    const delay = 10 * seconds;
    const animationInterval = setInterval(() => {
      this.move();
      if (this._actualMoves >= this._expectedMoves) {
        clearInterval(animationInterval);
        this._isAnimating = false;
        this._actualMoves = 0;
        this._destination = undefined;
        this._onAnimationComplete();
      }
    }, delay);

    // Store interval reference for cleanup
    this.timeouts.push(animationInterval as NodeJS.Timeout);
    this.interval = animationInterval as unknown as number;
  }

  public isWithinMapBounds(): boolean {
    if (!this._map) return true;

    const current = this.getLonLat();
    if (!current) return true;

    // Use map's contains method if available
    if (this._map.contains && typeof this._map.contains === 'function') {
      return this._map.contains(current);
    }

    // Fallback to extent checking for ESRI maps
    if (this._map.mapView && this._map.mapView.extent) {
      const extent = this._map.mapView.extent;
      return (
        current.lon >= extent.xmin &&
        current.lon <= extent.xmax &&
        current.lat >= extent.ymin &&
        current.lat <= extent.ymax
      );
    }

    // Default to true if we can't check bounds
    return true;
  }

  public getAnimationState(): { isAnimating: boolean; destination?: SnLonLat; seconds?: number } {
    return {
      isAnimating: this._isAnimating,
      destination: this._destination,
      seconds: this._expectedMoves > 0 ? this._expectedMoves / 10 : undefined, // Convert back to seconds
    };
  }

  // Type methods
  public setType(type: EsriMarkerType): void {
    this._type = type;
    this._updateMarkerSymbol();
  }

  public getType(): EsriMarkerType | undefined {
    return this._type;
  }

  // Address methods
  public setAddress(address: string): void {
    this.address = address;
  }

  public getAddress(): string | undefined {
    return this.address;
  }

  // Feature methods
  public setFeature(feature: unknown): void {
    this.feature = feature as Feature<Point>;
  }

  public getFeature(): unknown {
    return this.feature;
  }

  public getFeatures(): unknown {
    return this.feature;
  }

  // Pulse methods
  public setPulse(colors: string[], delay: number, fontSet?: string, fontIcon?: string): void {
    this.stopPulse();

    let colorIndex = 0;
    this.pulseInterval = setInterval(() => {
      // Implement pulse animation by changing marker color
      const color = colors[colorIndex % colors.length];
      this.setStyle({ fillColor: color });
      colorIndex++;
    }, delay) as unknown as number;

    this.timeouts.push(this.pulseInterval as unknown as NodeJS.Timeout);
  }

  public stopPulse(): void {
    if (this.pulseInterval) {
      clearInterval(this.pulseInterval);
      this.pulseInterval = undefined;
    }
  }

  // Custom data methods
  public setCustomData(data: unknown): void {
    if (this.marker) {
      this.marker.attributes = { ...this.marker.attributes, customData: data };
    }
  }

  public getCustomData(): unknown {
    return this.marker?.attributes?.customData;
  }

  // Status badge methods
  public setStatusBadge(status: string, badgePosition?: Record<string, string>): void {
    this._badge = status as SnBadgeConfig;
    if (badgePosition) {
      this.badgePosition = { ...this.badgePosition, ...badgePosition };
    }
    this._createBadge();
  }

  public getStatusBadge(): { status?: string; position?: Record<string, string> } {
    return {
      status: this._badge as string | undefined,
      position: this.badgePosition,
    };
  }

  public clearStatusBadge(): void {
    this._badge = undefined;
    // Remove badge from marker
  }

  // Private helper methods
  private _generateId(): string {
    return `esri-marker-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  private _onAnimationComplete(): void {
    // Update timestamp when animation completes
    this.updated = Date.now();

    // Clean up animation state
    this._destination = undefined;
    this._actualMoves = 0;
    this._expectedMoves = 0;
    this._deltaLat = 0;
    this._deltaLng = 0;
  }

  private _createMarker(): void {
    // Create basic ESRI Graphic
    const position = this._options.position;

    if (!position) {
      throw new Error(
        'FsAdvancedEsriMarker: Position is required. Please provide a position in the options.',
      );
    }

    const convertedPosition = SnLonLat.convert(position);

    this.marker = new this.esri.Graphic({
      geometry: {
        type: 'point',
        longitude: convertedPosition.lon,
        latitude: convertedPosition.lat,
      } as InstanceType<EsriModules['Point']>,
      attributes: {
        id: this.id,
        title: this._title,
      },
    });

    this._updateMarkerSymbol();
  }

  private _updateMarkerSymbol(): void {
    if (!this.marker) return;

    // Create appropriate symbol based on icon type and style
    const symbolProperties = {
      type: 'simple-marker' as const,
      color: this.color || '#fff',
      size: parseInt(this.size) || 24,
      outline: this.outline
        ? {
            color: this.styles?.strokeColor || '#000',
            width: this.styles?.strokeWeight || 1,
          }
        : undefined,
    };

    if (this.rotation) {
      (symbolProperties as Record<string, unknown>).angle = this.rotation;
    }

    this.marker.symbol = new this.esri.SimpleMarkerSymbol(symbolProperties);
  }

  private _createClusterMarker(): void {
    this.content.innerHTML = `<span class="fs ab3"></span>`;
    this.content.classList.add('fs__advanced-axon-cluster-marker');
    this._createBadge();
  }

  private _createImageMarker(): void {
    if (this._isImageIcon(this.icon)) {
      this.content.innerHTML = `<img src="${this.icon.url}" />`;
      this.content.classList.add('fs__center-marker');
      this.content.classList.add('fs__advanced-marker');
      this.content.classList.add('fs__advanced-feature-image-marker');
    }
  }

  private _createIconMarker(): void {
    if (this._isFontIcon(this.icon)) {
      this.content.innerHTML = `<i class="${this.icon.fontSet} ${this.icon.fontIcon}"></i>`;
    } else {
      // Default icon implementation
      this.content.innerHTML = `<i class="fa-solid fa-map-pin"></i>`;
    }

    this.content.classList.add('fs__center-marker');
    this.content.classList.add('fs__advanced-marker');
    this.content.style.color = this.color;

    if (this.iconLabel) {
      this._createIconLabel();
    }
  }

  private _createIconLabel(): void {
    if (!this.iconLabel) return;

    const label = document.createElement('span');
    label.textContent = this.iconLabel.text;
    label.style.position = 'absolute';
    label.style.top = `${this.iconLabel.top || 0}px`;
    label.style.left = `${this.iconLabel.left || 0}px`;
    label.style.color = this.iconLabel.color || '#000';
    label.style.fontSize = this.iconLabel.fontSize || '12px';

    if (this.iconLabel.transform) {
      label.style.transform = this.iconLabel.transform;
    }

    this.content.appendChild(label);
  }

  private _createBadge(): void {
    if (!this._badge) return;

    const badge = document.createElement('span');
    badge.textContent = this._badge as string;
    badge.classList.add('fs__marker-badge');
    badge.style.position = 'absolute';
    badge.style.right = this.badgePosition.right;
    badge.style.bottom = this.badgePosition.bottom;
    badge.style.top = this.badgePosition.top;
    badge.style.left = this.badgePosition.left;

    this.content.appendChild(badge);
    this.badge = badge;
  }

  private _cleanup(): void {
    // Clear all timeouts and intervals
    this.timeouts.forEach((timeout) => clearTimeout(timeout));
    this.timeouts = [];

    if (this.pulseInterval) {
      clearInterval(this.pulseInterval);
      this.pulseInterval = undefined;
    }

    // Complete observables
    this._destroy$.next();
    this._destroy$.complete();
    this._latLngChange$.complete();
    this._removed$.complete();
  }

  protected _isImageIcon(
    icon: EsriMapMarkerIcon | undefined,
  ): icon is { url: string } & EsriMapMarkerIcon {
    return (
      typeof icon === 'object' && icon !== null && 'url' in icon && typeof icon.url === 'string'
    );
  }

  protected _isFontIcon(
    icon: EsriMapMarkerIcon | undefined,
  ): icon is { fontSet: string; fontIcon: string } & EsriMapMarkerIcon {
    return typeof icon === 'object' && icon !== null && 'fontSet' in icon && 'fontIcon' in icon;
  }
}
